import React, { useState, useEffect } from 'react';
import { MapContainer, TileLayer, Marker, Polyline, useMap } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import './RequestDetailsPage.css';
import { useLanguage } from './i18n/LanguageContext';
import {
  ridePhaseOf,
  hasAssignedVendor,
  hasAssignedCar,
  hasAssignedDriver,
  RIDE_PHASE
} from './utils/rides';

const mapDot = (color, kind) => L.divIcon({
  className: `request-map-dot${kind === 'drop' ? ' is-drop' : ''}`,
  html: `<span style="background:${color}"></span>`,
  iconSize: [18, 18],
  iconAnchor: [9, 9]
});

const FitRoute = ({ pickup, drop }) => {
  const map = useMap();
  const pickupLat = pickup[0];
  const pickupLng = pickup[1];
  const dropLat = drop[0];
  const dropLng = drop[1];
  useEffect(() => {
    const bounds = L.latLngBounds([[pickupLat, pickupLng], [dropLat, dropLng]]);
    if (bounds.isValid()) {
      map.fitBounds(bounds, { padding: [72, 72], maxZoom: 14 });
    }
  }, [map, pickupLat, pickupLng, dropLat, dropLng]);
  return null;
};

const coordinate = (...values) => {
  for (const value of values) {
    const number = parseFloat(value);
    if (Number.isFinite(number)) return number;
  }
  return null;
};

const RequestDetailsPage = ({ ride, onBack, onCancelRequest, onHelpContact, onTrackDriver }) => {
  const { t } = useLanguage();
  const [arrivalTime, setArrivalTime] = useState(null);
  const [showCancelConfirm, setShowCancelConfirm] = useState(false);
  const [isCancelling, setIsCancelling] = useState(false);
  const [isWide, setIsWide] = useState(() => window.matchMedia('(min-width: 768px)').matches);

  useEffect(() => {
    const media = window.matchMedia('(min-width: 768px)');
    const onChange = () => setIsWide(media.matches);
    media.addEventListener('change', onChange);
    return () => media.removeEventListener('change', onChange);
  }, []);

  useEffect(() => {
    // Calculate arrival time (mock - you can replace with actual API data)
    if (ride && ridePhaseOf(ride) === RIDE_PHASE.CAR_ASSIGNED) {
      // Mock: Driver arriving in 15 minutes
      const now = new Date();
      const arrival = new Date(now.getTime() + 15 * 60000); // 15 minutes from now
      setArrivalTime(arrival);
    }
  }, [ride]);

  if (!ride) {
    return null;
  }

  const getStatusSteps = () => {
    const phase = ridePhaseOf(ride);

    return {
      requestSubmitted: {
        completed: true,
        label: t('Request Submitted')
      },
      assigningDriver: {
        completed: phase === RIDE_PHASE.CAR_ASSIGNED || phase === RIDE_PHASE.COMPLETED,
        current: phase === RIDE_PHASE.WAITING_VENDOR || phase === RIDE_PHASE.VENDOR_ASSIGNED,
        label: t('Assigning a Driver')
      },
      driverAssigned: {
        completed: phase === RIDE_PHASE.COMPLETED,
        current: phase === RIDE_PHASE.CAR_ASSIGNED,
        label: t('Driver Assigned! Arriving in XX mins.'),
        showTrack: phase === RIDE_PHASE.CAR_ASSIGNED
      },
      rideCompleted: {
        completed: phase === RIDE_PHASE.COMPLETED,
        current: false,
        label: t('Ride Completed!')
      }
    };
  };

  const steps = getStatusSteps();
  const phase = ridePhaseOf(ride);
  const isActiveRide = phase === RIDE_PHASE.WAITING_VENDOR
    || phase === RIDE_PHASE.VENDOR_ASSIGNED
    || phase === RIDE_PHASE.CAR_ASSIGNED;

  const formatExpectedTime = () => {
    const source = ride.carRequestTime || ride.pickupTime || ride.scheduledTime || ride.createdAt;
    if (!source) return 'Not specified';
    const date = new Date(source);
    if (Number.isNaN(date.getTime())) return source;
    const time = date.toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    });
    const day = date.toLocaleDateString('en-GB', {
      day: 'numeric',
      month: 'short',
      year: 'numeric'
    });
    return `${time}, ${day}`;
  };

  const formatArrivalTime = () => {
    if (arrivalTime) {
      const minutes = Math.ceil((arrivalTime - new Date()) / 60000);
      return minutes > 0 ? minutes : 0;
    }
    return 'XX';
  };

  const pickupLat = coordinate(ride.startLatitude, ride.pickupLatitude, ride.pickupLocation?.latitude);
  const pickupLng = coordinate(ride.startLongitude, ride.pickupLongitude, ride.pickupLocation?.longitude);
  const dropLat = coordinate(ride.endLatitude, ride.dropLatitude, ride.dropLocation?.latitude);
  const dropLng = coordinate(ride.endLongitude, ride.dropLongitude, ride.dropLocation?.longitude);
  const hasRoute = [pickupLat, pickupLng, dropLat, dropLng].every((value) => value !== null);

  return (
    <div className={`request-details-page${isWide ? ' is-wide' : ''}`}>
      {isWide && (
        <div className="request-map">
          {hasRoute && (
            <MapContainer
              center={[(pickupLat + dropLat) / 2, (pickupLng + dropLng) / 2]}
              zoom={12}
              style={{ height: '100%', width: '100%' }}
              scrollWheelZoom={false}
              zoomControl={false}
            >
              <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
              <FitRoute pickup={[pickupLat, pickupLng]} drop={[dropLat, dropLng]} />
              <Polyline
                positions={[[pickupLat, pickupLng], [dropLat, dropLng]]}
                pathOptions={{ color: '#5b6cff', weight: 3, dashArray: '7 8' }}
              />
              <Marker position={[pickupLat, pickupLng]} icon={mapDot('#22a559', 'pickup')} />
              <Marker position={[dropLat, dropLng]} icon={mapDot('#e23d4a', 'drop')} />
            </MapContainer>
          )}
        </div>
      )}

      <div className="request-details-content">
        <button className="back-button request-back" onClick={onBack} aria-label="Go back">
          <img src={process.env.PUBLIC_URL + '/images/back_button.svg'} alt="" />
        </button>

        <div className="request-card">
          <h1 className="request-details-title">{t('Request Details')}</h1>

          {hasAssignedVendor(ride) ? (
            <p className="assigned-vendor">{ride.vendorName}</p>
          ) : null}
          {hasAssignedCar(ride) ? (
            <p className="assigned-car">{[ride.carName, ride.carNumber].filter(Boolean).join(' • ')}</p>
          ) : null}
          {hasAssignedDriver(ride) ? (
            <p className="assigned-driver">{ride.driverName} {ride.driverMobileNumber || ''}</p>
          ) : null}

          <div className="expected-time-section">
            <label className="section-label">{t('Expected Time')}</label>
            <div className="expected-time-box">
              <svg className="calendar-icon" width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <rect x="3" y="4" width="18" height="18" rx="2" stroke="currentColor" strokeWidth="2"/>
                <line x1="16" y1="2" x2="16" y2="6" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                <line x1="8" y1="2" x2="8" y2="6" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                <line x1="3" y1="10" x2="21" y2="10" stroke="currentColor" strokeWidth="2"/>
              </svg>
              <span className="expected-time-text">{formatExpectedTime()}</span>
            </div>
          </div>

          <div className="progress-section">
            <h2 className="section-title">{t('Progress')}</h2>
            <div className="progress-steps">
              <div className="progress-step">
                <div className="step-indicator completed">
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M20 6L9 17L4 12" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </div>
                <div className="step-content">
                  <p className="step-label">{steps.requestSubmitted.label}</p>
                </div>
              </div>
              <div className={`step-connector${steps.requestSubmitted.completed ? ' done' : ''}`}></div>

              <div className="progress-step">
                <div className={`step-indicator ${steps.assigningDriver.completed ? 'completed' : steps.assigningDriver.current ? 'current' : ''}`}>
                  {steps.assigningDriver.completed ? (
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M20 6L9 17L4 12" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  ) : steps.assigningDriver.current ? <div className="current-dot"></div> : null}
                </div>
                <div className="step-content">
                  <p className="step-label">{steps.assigningDriver.label}</p>
                  {steps.assigningDriver.current && <p className="current-status-text">{t('Current Status')}</p>}
                </div>
              </div>
              <div className={`step-connector${steps.assigningDriver.completed ? ' done' : ''}`}></div>

              <div className="progress-step">
                <div className={`step-indicator ${steps.driverAssigned.completed ? 'completed' : steps.driverAssigned.current ? 'current' : ''}`}>
                  {steps.driverAssigned.completed ? (
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M20 6L9 17L4 12" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  ) : steps.driverAssigned.current ? <div className="current-dot"></div> : null}
                </div>
                <div className="step-content">
                  <p className="step-label">{steps.driverAssigned.label.replace('XX', formatArrivalTime())}</p>
                  {steps.driverAssigned.showTrack ? (
                    <button type="button" className="track-driver-link" onClick={() => onTrackDriver && onTrackDriver(ride)}>
                      {t('Track Driver >')}
                    </button>
                  ) : (
                    <span className="track-driver-link is-muted">{t('Track Driver >')}</span>
                  )}
                </div>
              </div>
              <div className={`step-connector${steps.driverAssigned.completed ? ' done' : ''}`}></div>

              <div className="progress-step">
                <div className={`step-indicator ${steps.rideCompleted.completed ? 'completed' : ''}`}>
                  {steps.rideCompleted.completed && (
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M20 6L9 17L4 12" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  )}
                </div>
                <div className="step-content">
                  <p className="step-label">{steps.rideCompleted.label}</p>
                </div>
              </div>
            </div>
          </div>

          {isActiveRide && (
            <div className="request-actions">
              <button className="help-button" onClick={onHelpContact}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                  <path d="M4 13V11a8 8 0 0 1 16 0v2" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                  <rect x="2" y="13" width="5" height="7" rx="2" stroke="currentColor" strokeWidth="2"/>
                  <rect x="17" y="13" width="5" height="7" rx="2" stroke="currentColor" strokeWidth="2"/>
                </svg>
                <span>{t('Help / Contact Us')}</span>
              </button>
              <button
                className="cancel-button"
                onClick={() => setShowCancelConfirm(true)}
                disabled={isCancelling}
              >
                {isCancelling ? t('Cancelling...') : t('Cancel request')}
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Cancel Confirmation Popup */}
      {showCancelConfirm && (
        <div className="cancel-confirm-overlay" onClick={() => !isCancelling && setShowCancelConfirm(false)}>
          <div className="cancel-confirm-popup" onClick={(e) => e.stopPropagation()}>
            <h3 className="confirm-title">{t('Cancel Request')}</h3>
            <p className="confirm-message">
              Are you sure you want to cancel this request? This action cannot be undone.
            </p>
            <div className="confirm-actions">
              <button 
                className="confirm-btn-cancel"
                onClick={() => setShowCancelConfirm(false)}
                disabled={isCancelling}
              >
                No, Keep Request
              </button>
              <button 
                className="confirm-btn-confirm"
                onClick={async () => {
                  setIsCancelling(true);
                  try {
                    await onCancelRequest(ride.bookingId || ride.id);
                    setShowCancelConfirm(false);
                  } catch (error) {
                    // Error is handled in App.js
                  } finally {
                    setIsCancelling(false);
                  }
                }}
                disabled={isCancelling}
              >
                {isCancelling ? 'Cancelling...' : 'Yes, Cancel Request'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default RequestDetailsPage;
